/**
 * ChallengeCompleteModalV2 - Challenge completion modal
 *
 * Features:
 * - Final results summary
 * - Celebratory animation
 * - Navigation to report
 */

import { memo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'

// Trophy icon
const TrophyIcon = () => (
  <svg className="w-20 h-20" viewBox="0 0 80 80" fill="none">
    <circle cx="40" cy="40" r="36" className="fill-amber-100 dark:fill-amber-900/30" />
    <path
      d="M40 20c-8 0-15 7-15 15v5c0 8 7 15 15 15s15-7 15-15v-5c0-8-7-15-15-15z"
      className="fill-amber-500"
    />
    <path d="M35 60h10v8H35v-8z" className="fill-amber-600" />
    <path d="M28 68h24v4H28v-4z" className="fill-amber-700" />
    <path
      d="M20 30c-3 0-5 2-5 5v3c0 5 4 8 8 8"
      className="stroke-amber-500"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M60 30c3 0 5 2 5 5v3c0 5-4 8-8 8"
      className="stroke-amber-500"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
)

const ChallengeCompleteModalV2 = memo(() => {
  const navigate = useNavigate()

  // Store state
  const showChallengeCompleteModal = useChallengeStoreV2(
    (state) => state.showChallengeCompleteModal
  )
  const currentChallenge = useChallengeStoreV2((state) => state.currentChallenge)
  const stages = useChallengeStoreV2((state) => state.stages)

  // Store actions
  const closeChallengeCompleteModal = useChallengeStoreV2(
    (state) => state.closeChallengeCompleteModal
  )
  const resetAll = useChallengeStoreV2((state) => state.resetAll)

  // Calculate stats from completed stages
  const totalQuestions = stages.reduce((sum, stage) => sum + (stage.responsesCount || 0), 0)
  const totalCorrect = stages.reduce((sum, stage) => sum + (stage.correctCount || 0), 0)
  const overallScore =
    stages.length > 0
      ? stages.reduce((sum, stage) => sum + (stage.score || 0), 0) / stages.length
      : 0

  // Handle view report
  const handleViewReport = useCallback(() => {
    closeChallengeCompleteModal()
    resetAll()
    if (currentChallenge?.id) {
      navigate(`/challenge/${currentChallenge.id}/report`)
    } else {
      navigate('/dashboard')
    }
  }, [closeChallengeCompleteModal, resetAll, currentChallenge?.id, navigate])

  // Handle return to dashboard
  const handleDashboard = useCallback(() => {
    closeChallengeCompleteModal()
    resetAll()
    navigate('/dashboard')
  }, [closeChallengeCompleteModal, resetAll, navigate])

  // Prevent body scroll
  useEffect(() => {
    if (showChallengeCompleteModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showChallengeCompleteModal])

  if (!showChallengeCompleteModal) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop with confetti effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 via-purple-900/90 to-pink-900/90 backdrop-blur-sm" />

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              backgroundColor: ['#fbbf24', '#f97316', '#ef4444', '#8b5cf6', '#3b82f6'][i % 5],
              animationDelay: `${Math.random() * 2}s`,
              opacity: 0.6
            }}
          />
        ))}
      </div>

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center animate-[modalIn_0.3s_ease-out]">
        {/* Trophy animation */}
        <div className="flex justify-center mb-6 animate-bounce">
          <TrophyIcon />
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600 mb-2">
          Congratulations!
        </h2>

        <p className="text-xl text-gray-700 dark:text-gray-300 mb-2">Challenge Complete</p>

        <p className="text-gray-500 dark:text-gray-400 mb-8">
          You've completed all stages. Great job!
        </p>

        {/* Final Stats */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                {Math.round(overallScore)}%
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Score</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                {totalCorrect}/{totalQuestions}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Correct</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {stages.length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Stages</div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleViewReport}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
          >
            View Full Report
          </button>
          <button
            onClick={handleDashboard}
            className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl transition-all duration-200"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  )
})

ChallengeCompleteModalV2.displayName = 'ChallengeCompleteModalV2'

export default ChallengeCompleteModalV2
